import React, { useEffect, useState } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import { connect } from 'react-redux'
import {
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Typography,
  Button,
  IconButton,
  Dialog,
} from '@material-ui/core'
import NavigationPrompt from 'react-router-navigation-prompt'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import TeaserHeader from 'components/Teaser/TeaserHeader'
import { PlayerStatisticsWidget, SkillRatingsWidget } from 'containers'
import { PerformanceWidget } from 'components'
import CloseIcon from '@material-ui/icons/Close'
import { players as playersModule } from 'redux/modules'
import { fetchPlayerStatistics } from 'api/players/api'
import { createSubUrl } from 'utils/urlConstructor'

import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import {
  PlayerFitnessMedical,
  PlayerDevelopmentTracking,
  Sequences,
  MatchDayJournal,
  TopBar,
  PlayerSummary,
  PlayerDetails,
} from './components'
import useEditablePlayer from './useEditablePlayerHook'
import { TEAM_DASHBOARD_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(0.5),
    color: theme.palette.grey[500],
  },
})
const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h4">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
})

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
  },
  PerformanceWrap: {
    '& .performance-chart': {
      width: '100% !important',
    },
    '& .performance-widget-style': {
      paddingBottom: `${theme.spacing(0)} !important`,
      marginBottom: `${theme.spacing(1)} !important`,
      height: '100% !important',
    },
  },
  wBoardButton: {
    display: 'block',
    margin: `${theme.spacing(3)} auto`,
    width: theme.spacing(14),
  },
}))

const PlayerDashboardContainer = ({
  playerId, // Value either id or uuid
  history,
  match,
  match: {
    params: { seasonId },
  },
  fromUrl,
  currentPlayer,
  updatePlayer,
  deletePlayer,
  fetchTeamPlayer,
  language,
  timezone,
  seasons,
}) => {
  const [editablePlayer, changeEditablePlayer] = useEditablePlayer(
    currentPlayer,
    {}
  )
  const {
    strengths,
    weaknesses,
    development_tracking: developmentTracking,
  } = editablePlayer

  const [anyChange, setAnyChange] = useState(false)
  const [seasonUuid, setSeasonUuid] = useState(null)
  const [statistics, setStatistics] = useState({})

  useEffect(() => {
    if (seasons.length > 0) {
      const season = seasons.find((s) => s.id === +seasonId)
      setSeasonUuid(season.uuid)
    }
  }, [seasons])

  const featureGuard = useFeatureAccessGuard()
  const { enabled: hasShowTeaserHeaderEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.teasers_enabled
  )
  const { enabled: hasPlayerStatisticsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_statistics_enabled
  )
  const { enabled: hasVideoSectionEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.video_manager_enabled
  )
  const { enabled: hasPlayerProfileExportEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.player_profile_export_enabled
  )

  const onPlayerEdit = (fieldName, newValue) => {
    if (fieldName in editablePlayer) {
      changeEditablePlayer({ ...editablePlayer, [fieldName]: newValue })
    }
    setAnyChange(true)
  }

  const handleSave = () => {
    setAnyChange(false)
    const editablePlayerTemp = {
      ...editablePlayer,
    }

    const response = updatePlayer(currentPlayer.uuid, editablePlayerTemp)
    response.then(() => {
      setAnyChange(false)
    })

    return response
  }

  const handleShareClick = (e) => {
    if (featureGuard(hasPlayerProfileExportEnabled)) return

    e.preventDefault()
    e.stopPropagation()
    const shareUrl = `${process.env.REACT_APP_API_URL}/players/${currentPlayer.uuid}/share?language=${language}&time_zone=${timezone}&season=${seasonId}`
    window.open(shareUrl, '_blank')
  }

  const linkBack = fromUrl || createSubUrl(match, TEAM_DASHBOARD_ROUTE)

  const handleDelete = () =>
    deletePlayer(playerId).then(() => {
      history.push(linkBack)
    })
  const matchesPerformance =
    currentPlayer.statistics.matches_performance.filter((p) => p.rating > 0)

  const [value, setValue] = useState(0)
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }
  const classes = useStyles()

  useEffect(() => {
    if (!seasonUuid || !currentPlayer.uuid) return

    const fetchStatistics = async () => {
      try {
        const data = await fetchPlayerStatistics(currentPlayer.uuid, {
          season_uuid: seasonUuid,
        })
        setStatistics(data)
      } catch (err) {
        console.error('Failed to fetch statistics:', err)
      }
    }

    fetchStatistics()
  }, [seasonUuid, currentPlayer.uuid])

  return (
    <>
      <NavigationPrompt when={anyChange}>
        {({ onConfirm, onCancel }) => {
          return (
            <>
              <Dialog aria-labelledby="dialog-player" open>
                <DialogTitle id="dialog-player" onClose={onCancel}>
                  <Translate id="dialog.unsaved.title" />
                </DialogTitle>
                <DialogContent dividers>
                  <Typography variant="p" component="p">
                    <Translate id="dialog.unsaved.content-player" />
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => handleSave().then(onCancel)}
                    size="large"
                    variant="contained"
                    className={classes.wBoardButton}
                    color="primary"
                  >
                    <Translate id="dialog.unsaved.save-player" />
                  </Button>
                  <Button onClick={onConfirm} size="large">
                    <Translate id="dialog.unsaved.leave" />
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )
        }}
      </NavigationPrompt>

      {hasShowTeaserHeaderEnabled && (
        <Grid item xs={12} md={12} lg={12}>
          <TeaserHeader />
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <TopBar
            handleSave={handleSave}
            onShare={handleShareClick}
            id={playerId}
            deletePlayer={handleDelete}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={3}>
          <PlayerSummary
            player={{ ...currentPlayer }}
            handleDelete={handleDelete}
            updatePlayer={updatePlayer}
            handleSave={handleSave}
            changeEditablePlayer={changeEditablePlayer}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={3}>
          <PlayerDetails
            player={{ ...currentPlayer }}
            statistics={currentPlayer.statistics}
          />
        </Grid>
        {hasPlayerStatisticsEnabled && (
          <Grid item xs={12} md={12} lg={6}>
            <Card className={classes.card}>
              <Translate>
                {({ translate }) => (
                  <>
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label={translate('team.main-stats')} />
                    </Tabs>
                    <Divider />
                  </>
                )}
              </Translate>
              <CardContent>
                {value === 0 && (
                  <PlayerStatisticsWidget
                    statistics={statistics}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={12} lg={6}>
          <SkillRatingsWidget currentPlayer={currentPlayer} />
        </Grid>

        {hasPlayerStatisticsEnabled && (
          <Grid item xs={12} md={12} lg={6} className={classes.PerformanceWrap}>
            <Translate>
              {({ translate }) => (
                <PerformanceWidget
                  title={translate('team.player-match-performance')}
                  match={currentPlayer.statistics}
                  data={matchesPerformance}
                />
              )}
            </Translate>
          </Grid>
        )}
        {hasPlayerStatisticsEnabled && (
          <Grid item xs={12} md={12} lg={6}>
            <PlayerFitnessMedical
              onChange={onPlayerEdit}
              fitnessNmedicalRecords={currentPlayer.fitnessNmedicalRecords}
              physicalAge={currentPlayer.physique_age}
              physicalHeight={currentPlayer.physique_height}
              physicalWeight={currentPlayer.physique_weight}
              playerId={currentPlayer.id}
              playerUuid={currentPlayer.uuid}
            />
          </Grid>
        )}
        <Grid item xs={12} md={12} lg={hasPlayerStatisticsEnabled ? 6 : 12}>
          <PlayerDevelopmentTracking
            onChange={onPlayerEdit}
            fetchTeamPlayer={fetchTeamPlayer}
            player={{ ...currentPlayer }}
            handleSave={handleSave}
            {...{
              development_tracking: developmentTracking,
              strengths,
              weaknesses,
            }}
          />
        </Grid>

        {hasVideoSectionEnabled && (
          <Grid item xs={12} md={12} lg={12}>
            <Sequences sequences={currentPlayer.sequences} />
          </Grid>
        )}
        {hasPlayerStatisticsEnabled && (
          <Grid item xs={12} md={12} lg={12}>
            <MatchDayJournal journal={currentPlayer.journal} />
          </Grid>
        )}
      </Grid>
    </>
  )
}

PlayerDashboardContainer.defaultProps = {
  fromUrl: '',
  fetchTeamPlayer: () => 0,
}

PlayerDashboardContainer.propTypes = {
  playerId: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
  ]).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape().isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  fromUrl: PropTypes.string,
  currentPlayer: PropTypes.shape().isRequired,
  updatePlayer: PropTypes.func.isRequired,
  deletePlayer: PropTypes.func.isRequired,
  fetchTeamPlayer: PropTypes.func,
  timezone: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
}

export default connect(
  ({
    team,
    router: {
      location: { fromUrl },
    },
    players,
    auth,
    seasons: { items },
  }) => ({
    fromUrl,
    team,
    currentPlayer: players.current,
    timezone: auth.user.tz || auth.user.time_zone,
    language: auth.user.language,
    seasons: items,
  }),
  {
    updatePlayer: playersModule.updatePlayer,
    deletePlayer: playersModule.deletePlayer,
    fetchTeamPlayer: playersModule.fetchTeamPlayer,
  }
)(PlayerDashboardContainer)
